import {
  getData,
  getErrorMessage,
  getUrlWithQuery,
  getWithHeaders,
  post,
  postData,
  postNoResponse,
  toggleUnnestedAPINewEndpoint,
} from "common/services";
import { NestingPrefs, QuoteDetails, SelectedItem, ShipToFormValues } from "../QuoteResults/utils";
import {
  ItemOptionsUri,
  itemsEntity,
  MuleNestingApiUri,
  NestingApiUri,
  NestedQuoteUri,
  QuoteSelectionsUri,
  RequestDateUri,
  MuleResponse,
  NestedData,
  UnnestedFile,
} from ".";
import { normalize } from "normalizr";
import { datadogLogs } from "@datadog/browser-logs";
import { getTransportLabel } from "./transportOptions";

const callNewNestingAPI = async (
  prefs: NestingPrefs,
  nesting: UnnestedFile,
  shippingMethod: string,
  token: string,
  uid: string,
) => {
  const postBody = {
    onlyStandardLength: prefs.useStandardLength.toString(),
    maxLengthTolerable: prefs.maxNestedBeamLength,
    kerf: prefs.kerfWeight,
    gap: prefs.gap,
    endTrim: prefs.trimCut,
    shippingMethod: shippingMethod,
    quoteUid: uid,
  };
  const postResponse = await post(`${NestingApiUri}`, token, JSON.stringify(postBody), {
    "Content-Type": "application/json",
  });
  if (postResponse.error) {
    datadogLogs.logger.error("[API]: Error getting from nested data from nesting", {
      name: "Error: Error getting from nested data from nesting",
      details: postResponse.error,
    });
    return { error: postResponse.error.message?.toString() || "Unknown error", data: null };
  }
  return { error: null, data: postResponse.message?.data };
};

const callLegacyNestingAPI = async (
  prefs: NestingPrefs,
  nesting: UnnestedFile,
  shippingMethod: string,
  token: string,
) => {
  const rawParams = {
    onlyStandardLength: prefs.useStandardLength.toString(),
    maxLengthTolerable: prefs.maxNestedBeamLength?.toString() ?? "",
    kerf: prefs.kerfWeight?.toString() ?? "",
    gap: prefs.gap?.toString(),
    endTrim: prefs.trimCut?.toString(),
    id: nesting.uid ?? undefined,
    sameSequenceOnBar: "false",
    shippingMethod: shippingMethod,
  };
  const params = Object.fromEntries(
    Object.entries(rawParams).filter(([_, value]) => value !== undefined),
  ) as Record<string, string>;
  const getResponse = await getWithHeaders<MuleResponse<NestedData>>(
    getUrlWithQuery(`${MuleNestingApiUri}`, params),
    {},
    token,
  );
  if ("error" in getResponse.message.data) {
    datadogLogs.logger.error("[API]: Error getting from nested data from nesting API", {
      name: "Error: Error getting from nested data from nesting API",
      details: getResponse.message.data,
    });
    return { error: getResponse.message.data.error.description, data: null };
  }
  return { error: null, data: getResponse.message.data };
};

export const sendQuoteSelections = async (
  uid: string,
  token: string,
  selections: SelectedItem[],
) => {
  const error = await postNoResponse(
    `${QuoteSelectionsUri(uid)}`,
    token,
    JSON.stringify(selections),
  );
  if (error !== null) {
    datadogLogs.logger.error("[API]: Error send quote selections", {
      name: "Error: Send quote selections",
      details: error,
    });
    return getErrorMessage(error);
  }
  return error;
};
export const processNestedResults = async (
  uid: string,
  nesting: UnnestedFile,
  shippingMethod: string,
  prefs: NestingPrefs,
  token: string,
) => {
  let error: string | null = null;
  if (
    prefs.maxNestedBeamLength !== undefined &&
    prefs.kerfWeight !== undefined &&
    prefs.gap !== undefined &&
    prefs.trimCut !== undefined &&
    shippingMethod !== null
  ) {
    try {
      let nestedData: NestedData | null = null;
      const result = toggleUnnestedAPINewEndpoint
        ? await callNewNestingAPI(prefs, nesting, shippingMethod, token, uid)
        : await callLegacyNestingAPI(prefs, nesting, shippingMethod, token);
      if (result.error) {
        error = result.error;
      } else {
        nestedData = result.data;
      }
      if (!error && nestedData) {
        error = await postNoResponse(`${NestedQuoteUri(uid)}`, token, JSON.stringify(nestedData));
      }
    } catch (ex: any) {
      datadogLogs.logger.error("[API]: Error getting from nested data from nesting API", {
        name: "Error: Error getting from nested data from nesting API",
        details: ex,
      });
      error = ex;
    }
  }
  if (error !== null) return getErrorMessage(error);
  return error;
};

const sendQuotePreferences = async (uid: string, token: string, values: ShipToFormValues) => {
  const payload = new FormData();
  payload.set("uid", uid);
  payload.set("ship_to", values.shipTo);
  const transportationLabel = getTransportLabel(values.transportation);
  payload.set("transportation", transportationLabel);
  payload.set("request_date", values.requestDate);
  payload.set("city_id", values.cityId);
  if (values.soldToId) {
    payload.set("sold_to_id", values.soldToId);
  }
  if (values.soldToName) {
    payload.set("sold_to_name", values.soldToName);
  }
  const url = `${RequestDateUri}`;
  return await postData(url, token, payload);
};

const getItemOptions = async (uid: string, token: string) => {
  let { data, error } = await getData(`${ItemOptionsUri(uid)}`, token);
  if (data) {
    data = normalize(data, [itemsEntity]);
    return { data: data as QuoteDetails, error };
  }
  return { data: null, error };
};

export const sendQuoteRequest = async (uid: string, token: string, values: ShipToFormValues) => {
  let result = await sendQuotePreferences(uid, token, values);
  if (!result.error) {
    return await getItemOptions(uid, token);
  }
  return result;
};
